export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ResumeCredential {
  resumeToken: string;
  sessionId: string;
}

export interface IdentitySnapshot {
  clientId: string;
  resume?: ResumeCredential;
}

const CLIENT_ID_KEY = "klass-share.client-id.v1";
const RESUME_PREFIX = "klass-share.resume.v1.";

export type UuidFactory = () => string;
export type ConnIdFactory = () => string;

export function createConnId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join(
    "",
  );
}

export class IdentityStore {
  readonly #storage: KeyValueStorage;
  readonly #uuid: UuidFactory;

  public constructor(
    storage: KeyValueStorage,
    uuid: UuidFactory = createUuidV4,
  ) {
    this.#storage = storage;
    this.#uuid = uuid;
  }

  public load(hostId: string): IdentitySnapshot {
    let clientId = this.#storage.getItem(CLIENT_ID_KEY);
    if (!isUuidV4(clientId) || clientId.toLowerCase() === "host") {
      clientId = this.#newClientId();
    }
    const rawResume = this.#storage.getItem(this.#resumeKey(hostId));
    const resume = parseResume(rawResume);
    return { clientId, ...(resume === undefined ? {} : { resume }) };
  }

  public saveResume(hostId: string, credential: ResumeCredential): void {
    this.#storage.setItem(this.#resumeKey(hostId), JSON.stringify(credential));
  }

  public clearResume(hostId: string): void {
    this.#storage.removeItem(this.#resumeKey(hostId));
  }

  /** Normative RESUME_UNAUTHORIZED recovery: abandon, never retry, the contested id. */
  public replaceContestedIdentity(hostId: string): IdentitySnapshot {
    this.clearResume(hostId);
    return { clientId: this.#newClientId() };
  }

  #newClientId(): string {
    const clientId = this.#uuid();
    if (!isUuidV4(clientId) || clientId.toLowerCase() === "host") {
      throw new Error("UUID factory returned an invalid v4 clientId");
    }
    this.#storage.setItem(CLIENT_ID_KEY, clientId);
    return clientId;
  }

  #resumeKey(hostId: string): string {
    return `${RESUME_PREFIX}${hostId.toUpperCase()}`;
  }
}

export function createUuidV4(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function isUuidV4(value: string | null): value is string {
  return (
    value !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
      value,
    )
  );
}

function parseResume(value: string | null): ResumeCredential | undefined {
  if (value === null) return undefined;
  try {
    const parsed = JSON.parse(value) as unknown;
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "resumeToken" in parsed &&
      "sessionId" in parsed &&
      typeof parsed.resumeToken === "string" &&
      typeof parsed.sessionId === "string" &&
      parsed.resumeToken.length > 0 &&
      parsed.sessionId.length > 0
    ) {
      return { resumeToken: parsed.resumeToken, sessionId: parsed.sessionId };
    }
  } catch {
    // Corrupt local state is intentionally treated as no credential.
  }
  return undefined;
}
